import { convertHEICToJPEG } from '@repo/utils';
import { Button, Icon } from '@successmode-ktp/kui';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import IMG_UPLOAD_EMPTY from '@/assets/uploadFile/img_upload_empty.png';
import GuideModal from '../MedicalUploadFile/Guide.modal';

type Props = {
  onChange: (value: File[]) => void;
  nationality?: string;
  isGuide?: boolean;
};

const UploadFile = ({
  onChange,
  nationality = 'KOR',
  isGuide = false,
}: Props) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isOpenGuide, setIsOpenGuide] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const openFileDialog = () => {
    setIsOpenGuide(false);
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  useEffect(() => {
    onChange(files);
  }, [files]);

  const isValidFileType = (type: string) =>
    ['pdf', 'image'].some((format) => type.includes(format));

  const uploadFile = async () => {
    const MAX_FILES = 3;
    let newFiles;
    if (inputRef.current?.files) {
      newFiles = [...inputRef.current.files];

      if (files.length + newFiles.length > MAX_FILES) {
        return alert(`최대 ${MAX_FILES} 개의 파일만 업로드할 수 있습니다.`);
      }

      newFiles.map(async (file) => {
        const { type } = file;
        if (isValidFileType(type)) {
          const imageFile =
            type === 'image/heic' ? await convertHEICToJPEG(file) : file;
          setFiles((prev) => [...prev, imageFile]);
        } else {
          alert('항공권 파일은 이미지 또는 PDF 형식만 업로드 가능합니다.');
        }
      });
    }
  };

  return (
    <div>
      <UploadWrapper>
        {files.length === 0 && (
          <EmptyWrapper>
            <img src={IMG_UPLOAD_EMPTY} alt='' />
            <InputPlaceholder>
              {nationality === 'KOR'
                ? '첨부된 파일이 없습니다.'
                : 'No files attached'}
            </InputPlaceholder>
          </EmptyWrapper>
        )}

        {files.length !== 0 &&
          files.map((file, index) => {
            return (
              <InputWrapper>
                {file.name}
                <button type='button'>
                  <Icon.XFilled
                    fillColor='#DDDDDD'
                    onClick={() =>
                      setFiles((prev) => {
                        const newItems = [...prev];
                        newItems.splice(index, 1);
                        return newItems;
                      })
                    }
                  />
                </button>
              </InputWrapper>
            );
          })}
        <Button
          type='button'
          onClick={() => (isGuide ? setIsOpenGuide(true) : openFileDialog())}
          style={{ gap: '8px', marginTop: 'auto', backgroundColor: '#3A3B3E' }}
        >
          <Icon.X
            fillColor='#fff'
            size={16}
            style={{ transform: 'rotate(45deg)' }}
          />
          {nationality === 'KOR' ? '파일 첨부' : 'Attach files'}
        </Button>
        <TicketInput
          key={JSON.stringify(files)}
          id='ticket-input'
          type='file'
          accept='.heic, image/*, application/pdf'
          onChange={uploadFile}
          ref={inputRef}
          multiple
        />
      </UploadWrapper>
      <GuideModal
        isOpen={isOpenGuide}
        onClose={() => setIsOpenGuide(false)}
        onClick={openFileDialog}
      />
    </div>
  );
};
export default UploadFile;

const InputPlaceholder = styled.span`
  color: var(--KTP-Grayscale-Gray_09, #ccc);
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.28px;
  margin: auto;
`;

const UploadWrapper = styled.div`
  display: flex;
  align-items: start;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  width: 100%;
  min-height: 230px;
  padding: 12px 16px;
  margin-top: 16px;
  border-radius: 8px;
  background-color: #fafafa;
  border: 1px dashed var(--KTP-Grayscale-Gray_09, #ccc);
  color: var(--KTP-Grayscale-Gray_03, #333);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TicketInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 32px auto 40px;
`;
